/**
 * vivana-quiz-engine.js — Vivana Quiz v3 scoring engine (pure, no DOM).
 *
 * Source of truth: context.md §3.5–3.8 + quiz-v3-qa-matrix.md.
 * Dual environment: attaches to window.VivanaQuizEngine in the browser and
 * exports via module.exports under Node (for tests/). Contains NO DOM access.
 *
 * Public API:
 *   computeResult(q0Value, answers, scoreMap) -> {
 *     track, outputType, primary, runnerUp, omega3,
 *     totals, adjusted, scores            // diagnostics
 *   }
 *   - q0Value: a track letter ('A' | 'B' | 'C'), as emitted by the context-gate
 *     block's `data-track`, or a legacy Q0 key ('woman_no_hrt' | 'woman_hrt' |
 *     'woman_nonhormonal' | 'man' | 'prefer_not').
 *   - answers: { '<question key>': '<option key>' }. Question keys are opaque
 *     (block ids in the storefront, 1..10 in the fixtures) and the question
 *     count is not fixed — an unanswered/unknown question contributes 0.
 *   - scoreMap: { '<question key>': { '<option key>': [BAL,COR,GUT,MAG,IMM,COL] } }
 *     The storefront passes the merchant-authored table emitted by
 *     sections/vivana-quiz.liquid as window.VivanaQuizConfig.scoreMap.
 *     Omitted -> DEFAULT_SCORE_MAP (the v3 table, kept for tests and as a
 *     safety net if a theme serves the section without a config).
 *   For A/B, primary/runnerUp are SYSTEM codes; for C they are PRODUCT codes
 *   (and `products` = [primary, runnerUp]).
 *
 *   ZERO-SIGNAL EXCEPTION: if every column scored 0 — "None of the above" (or an
 *   existing neutral answer) on every question — the result is Magnesium Pro plus
 *   Omega-3 and nothing else, on EVERY track: `outputType: 'products'`,
 *   `primary: 'MAG'`, `runnerUp: null`, `omega3: true`, `noSignal: true`.
 *   This is the one case where Tracks A and B return products rather than systems.
 */
(function (root, factory) {
  var engine = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  if (typeof window !== 'undefined') window.VivanaQuizEngine = engine;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Product score columns, fixed order.
  var COLS = ['BAL', 'COR', 'GUT', 'MAG', 'IMM', 'COL'];

  // ── Fallback scoring table (v3) — value-key -> [BAL,COR,GUT,MAG,IMM,COL] ─────
  // In the storefront the live table comes from the section's question blocks
  // (window.VivanaQuizConfig.scoreMap) and this is never consulted. It stays as
  // the canonical v3 fixture the unit tests assert against, and as a safety net.
  // `none` is the "None of the above" escape answer: it scores nothing, so the
  // question is simply disregarded. Several questions already had a zero-scoring
  // neutral answer (zen, effortless, glowing, reliable, well_oiled, no_exercise);
  // `none` is the same mechanism, offered on every question.
  var Z = [0, 0, 0, 0, 0, 0];
  var DEFAULT_SCORE_MAP = {
    1: { calm_then_not: [1,2,0,1,0,0], brain_night_shift: [1,1,0,2,0,0], digestion_personality: [0,0,3,0,0,0], colds_collectibles: [0,0,1,0,3,0], none: Z },
    2: { zen: Z, low_hum: [1,1,0,1,0,0], constantly_high: [1,3,0,1,0,0], sleep_coffee: [1,2,0,2,0,0], none: Z },
    3: { effortless: Z, three_am: [1,1,0,2,0,0], mental_podcast: [1,2,0,2,0,0], not_restorative: [1,1,0,1,0,0], none: Z },
    4: { mood: [3,1,0,0,0,0], stress: [1,3,0,1,0,0], digestion: [0,0,3,0,0,0], energy: [2,1,0,2,0,0], immune: [0,0,1,0,3,0], none: Z },
    5: { glowing: Z, dull_dry: [1,0,0,0,0,2], thinner: [1,0,0,0,0,3], fine_outside: [1,0,0,0,0,1], none: Z },
    6: { reliable: Z, bloated: [0,0,3,0,0,0], sensitive: [0,0,3,0,0,0], unpredictable: [0,0,2,0,0,0], none: Z },
    7: { sleep: [1,1,0,3,0,0], patience: [1,3,0,1,0,0], digestion: [0,0,3,0,0,0], immune: [0,0,1,0,3,0], recovery: [0,1,0,1,0,3], none: Z },
    8: { well_oiled: Z, stiff_morning: [0,0,0,2,0,2], creaky: [1,0,0,2,0,3], bones_posture: [1,0,0,1,0,3], none: Z },
    9: { physically_betrayed: [0,0,0,1,0,3], calmer: [1,2,0,1,0,0], exhausted: [1,1,0,2,0,1], no_exercise: Z, none: Z },
    10: { balanced: [3,1,0,1,0,0], calmer: [1,3,0,1,0,0], lighter: [0,0,3,0,0,0], stronger: [0,1,0,2,0,3], none: Z }
  };

  // ── Systems (Omega-3 scores 0; `count` includes Omega-3 for the tiebreaker) ──
  var SYSTEMS = {
    FOUNDATION:    { components: ['BAL', 'GUT', 'MAG'],               count: 3, hrt: false },
    STRESS:        { components: ['COR', 'GUT'],                      count: 3, hrt: true  },
    CORE_RESET:    { components: ['BAL', 'COR', 'GUT', 'MAG'],        count: 5, hrt: false },
    RESILIENCE:    { components: ['BAL', 'COR', 'COL', 'MAG'],        count: 5, hrt: false },
    COMPLETE:      { components: ['BAL', 'COR', 'GUT', 'COL', 'MAG'], count: 6, hrt: false },
    HRT_COMPANION: { components: ['COR', 'GUT', 'COL', 'MAG'],        count: 5, hrt: true  }
  };

  // Deterministic final tiebreak when score AND count tie (context.md §6.1).
  var SYSTEM_PRIORITY = ['COMPLETE', 'CORE_RESET', 'RESILIENCE', 'HRT_COMPANION', 'FOUNDATION', 'STRESS'];

  // Track C product eligibility + 2nd-place tiebreak order: COL > MAG > COR > GUT > IMM.
  var TRACK_C_PRODUCTS = ['COR', 'GUT', 'MAG', 'IMM', 'COL'];
  var TRACK_C_PRIORITY = ['COL', 'MAG', 'COR', 'GUT', 'IMM'];

  var TRACKS = ['A', 'B', 'C'];

  // Legacy Q0 value -> track, from before the context gate became a block whose
  // options carry their own track. Kept so the QA fixtures (and any cached page
  // still serving the old markup) keep resolving to the same track.
  var Q0_TRACK = {
    woman_no_hrt: 'A',
    prefer_not: 'A',
    woman_hrt: 'B',
    woman_nonhormonal: 'C',
    man: 'C'
  };

  // Accept either a track letter (from the gate block) or a legacy Q0 key.
  function resolveTrack(q0Value) {
    if (TRACKS.indexOf(q0Value) !== -1) return q0Value;
    return Q0_TRACK[q0Value] || 'A';
  }

  // Edge-case defaults when all product scores are equal at a NON-ZERO value
  // (context.md §3.8). The all-zero case is handled earlier by NO_SIGNAL below.
  var EDGE_DEFAULTS = {
    A: { primary: 'CORE_RESET', runnerUp: 'FOUNDATION' },
    B: { primary: 'HRT_COMPANION', runnerUp: 'STRESS' },
    C: { primary: 'MAG', runnerUp: 'GUT' }
  };

  // Zero-signal result: the shopper told us nothing — "None of the above" (or an
  // existing neutral answer) on every question. Ranking has nothing to work with,
  // so recommend the gentle baseline instead of the broadest bundle: Magnesium Pro
  // + Omega-3 and nothing else, on every track. Both are HRT-safe (only Balance+
  // is excluded on Track B, and MAG is itself an HRT Companion component).
  var NO_SIGNAL_PRODUCT = 'MAG';

  function emptyTotals() {
    return { BAL: 0, COR: 0, GUT: 0, MAG: 0, IMM: 0, COL: 0 };
  }

  // Sum the scoring table across however many questions were answered.
  // Iterates the answers, not a fixed 1..10 range, so the number of questions is
  // whatever the merchant configured. Unknown question/option keys score 0.
  function scoreAnswers(answers, scoreMap) {
    var t = emptyTotals();
    var map = scoreMap || DEFAULT_SCORE_MAP;
    answers = answers || {};
    for (var q in answers) {
      if (!Object.prototype.hasOwnProperty.call(answers, q)) continue;
      var key = answers[q];
      if (!key) continue;
      var row = map[q] && map[q][key];
      if (!row) continue;
      for (var i = 0; i < COLS.length; i++) t[COLS[i]] += row[i] || 0;
    }
    return t;
  }

  function allEqual(totals) {
    var first = totals[COLS[0]];
    for (var i = 1; i < COLS.length; i++) if (totals[COLS[i]] !== first) return false;
    return true;
  }

  // Every column scored zero — no usable signal at all.
  // MUST be evaluated on the RAW totals, never the per-track adjusted ones: Track C
  // adds +4 to COL, so `adjusted` is never all-zero there and the check would
  // silently never fire.
  function noSignal(totals) {
    for (var i = 0; i < COLS.length; i++) if (totals[COLS[i]] !== 0) return false;
    return true;
  }

  function systemScore(code, totals) {
    return SYSTEMS[code].components.reduce(function (sum, k) { return sum + totals[k]; }, 0);
  }

  // Rank systems for Track A (all) or Track B (hrt-eligible only).
  function rankSystems(totals, hrtOnly) {
    var codes = Object.keys(SYSTEMS).filter(function (c) { return hrtOnly ? SYSTEMS[c].hrt : true; });
    codes.sort(function (a, b) {
      var d = systemScore(b, totals) - systemScore(a, totals);
      if (d !== 0) return d;
      d = SYSTEMS[b].count - SYSTEMS[a].count;            // broader coverage wins
      if (d !== 0) return d;
      return SYSTEM_PRIORITY.indexOf(a) - SYSTEM_PRIORITY.indexOf(b); // fixed order
    });
    return codes;
  }

  // Rank products for Track C (BAL already excluded, COL already boosted).
  function rankProductsC(totals) {
    var codes = TRACK_C_PRODUCTS.slice();
    codes.sort(function (a, b) {
      var d = totals[b] - totals[a];
      if (d !== 0) return d;
      return TRACK_C_PRIORITY.indexOf(a) - TRACK_C_PRIORITY.indexOf(b);
    });
    return codes;
  }

  function computeResult(q0Value, answers, scoreMap) {
    var track = resolveTrack(q0Value);
    var totals = scoreAnswers(answers, scoreMap);

    // Adjusted totals per track: BAL zeroed on B & C; COL +4 on C.
    var adjusted = {
      BAL: totals.BAL, COR: totals.COR, GUT: totals.GUT,
      MAG: totals.MAG, IMM: totals.IMM, COL: totals.COL
    };
    if (track === 'B' || track === 'C') adjusted.BAL = 0;
    if (track === 'C') adjusted.COL += 4;

    // No signal at all → Magnesium Pro + Omega-3, nothing else, on every track.
    // Checked before `allEqual` because all-zero is a strict subset of all-equal:
    // this leaves the curated §3.8 defaults serving the all-equal-NON-ZERO case.
    if (noSignal(totals)) {
      return {
        track: track,
        outputType: 'products',
        primary: NO_SIGNAL_PRODUCT,
        runnerUp: null,                  // deliberately none — "and nothing else"
        products: [NO_SIGNAL_PRODUCT],
        omega3: true,                    // must be true even on A/B, so the card renders
        noSignal: true,                  // lets the DOM / future analytics react
        totals: totals,
        adjusted: adjusted
      };
    }

    var edge = allEqual(totals); // all columns equal at a non-zero value → curated defaults

    if (track === 'C') {
      var products = edge
        ? [EDGE_DEFAULTS.C.primary, EDGE_DEFAULTS.C.runnerUp]
        : rankProductsC(adjusted).slice(0, 2);
      return {
        track: 'C',
        outputType: 'products',
        primary: products[0],
        runnerUp: products[1],
        products: products,
        omega3: true,            // always auto-added on Track C
        totals: totals,
        adjusted: adjusted
      };
    }

    // Tracks A & B — systems output.
    var ranked, primary, runnerUp;
    if (edge) {
      primary = EDGE_DEFAULTS[track].primary;
      runnerUp = EDGE_DEFAULTS[track].runnerUp;
      ranked = rankSystems(adjusted, track === 'B');
    } else {
      ranked = rankSystems(adjusted, track === 'B');
      primary = ranked[0];
      runnerUp = ranked[1];
    }
    return {
      track: track,
      outputType: 'systems',
      primary: primary,
      runnerUp: runnerUp,
      omega3: false,             // embedded in systems, not a separate add-on
      totals: totals,
      adjusted: adjusted,
      scores: ranked.map(function (c) { return { code: c, score: systemScore(c, adjusted) }; })
    };
  }

  return {
    computeResult: computeResult,
    // exposed for tests / debugging
    scoreAnswers: scoreAnswers,
    rankSystems: rankSystems,
    rankProductsC: rankProductsC,
    resolveTrack: resolveTrack,
    noSignal: noSignal,
    DEFAULT_SCORE_MAP: DEFAULT_SCORE_MAP,
    SYSTEMS: SYSTEMS,
    Q0_TRACK: Q0_TRACK
  };
});
